/**
 * 候选人端 RTC 接入 hook。
 *
 * 行为:
 *   1. join()  —— 初始化 engine、加入房间、开摄像头和麦克风、发布音视频
 *   2. leave() —— 离会、清理
 *   3. 订阅"AI 面试官"的音频流(userId 以 ai_interviewer_ 开头)
 *
 * 注意:
 *   - Mock token(MOCK.* 开头)跳过真实 SDK 调用,仅供 UI 开发
 *   - 火山 Web SDK 加载失败(如无 @volcengine/rtc 包)时 gracefully degrade
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RTCToken } from '../api/types';

type RTCStatus = 'idle' | 'joining' | 'joined' | 'error' | 'left';

interface UseRTCOptions {
  tokenInfo: RTCToken | null;
  autoJoin?: boolean;
  onRemoteAudio?: (userId: string) => void;
}

export function useRTC({ tokenInfo, autoJoin, onRemoteAudio }: UseRTCOptions) {
  const [status, setStatus] = useState<RTCStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [aiPresent, setAiPresent] = useState(false);
  const [micLevel, setMicLevel] = useState(0);

  const engineRef = useRef<any>(null);
  const localVideoRef = useRef<HTMLDivElement | null>(null);
  const remoteAudioContainerRef = useRef<HTMLDivElement | null>(null);

  // 录音相关 refs(并行 2 路:① 候选人 mic / ② AI 远端 stream)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);  // 候选人 mic
  const recordingChunksRef = useRef<Blob[]>([]);
  const localMicStreamRef = useRef<MediaStream | null>(null);

  // 并行的 AI 流 MediaRecorder(直接录 RTC 远端音频流,不走 AudioContext 不混音)
  const aiRecorderRef = useRef<MediaRecorder | null>(null);
  const aiChunksRef = useRef<Blob[]>([]);
  const aiHookedRef = useRef(false);

  // 关键:两路录音启动时间戳(ms),用于上传时计算 ai 相对 mic 的偏移,后端 ffmpeg adelay 对齐
  const micStartedAtRef = useRef<number | null>(null);
  const aiStartedAtRef = useRef<number | null>(null);
  // 旧版 binary 收集(实测豆包不推音频,留着兼容,不会有数据)
  const aiAudioChunksRef = useRef<Uint8Array[]>([]);

  const isMock = tokenInfo?.token?.startsWith('MOCK.');

  const join = useCallback(async () => {
    if (!tokenInfo) return;
    setError(null);
    setStatus('joining');

    if (isMock) {
      console.warn('[useRTC] MOCK mode — skipping real RTC join');
      setStatus('joined');
      return;
    }

    try {
      // 火山 SDK 的静态枚举类型在不同版本有差异,统一用 any 绕过,真实字段 Spike 1 确认后收紧
      const mod = (await import('@volcengine/rtc')) as any;
      const VERTC = mod.default ?? mod;
      const engine = VERTC.createEngine(tokenInfo.app_id);
      engineRef.current = engine;

      const markAiIfMatch = (userId: string | undefined, source: string) => {
        if (!userId?.startsWith('ai_interviewer_')) return;
        console.log(`[useRTC] AI detected via ${source}:`, userId);
        setAiPresent(true);
        try {
          engine.subscribeStream?.(userId, VERTC.MediaType?.AUDIO ?? 1);
        } catch (err) {
          console.warn('[useRTC] subscribeStream threw (SDK may auto-subscribe):', err);
        }
        onRemoteAudio?.(userId);
      };

      // 多事件兜底 —— 不同 SDK 版本事件名有差异
      const eventNames = [
        'onUserPublishStream',
        'onUserJoined',
        'onUserStartAudioCapture',
        'onStreamAdd',
        'onStreamAdded',
        'onRemoteAudioStart',
      ];
      for (const name of eventNames) {
        const key = VERTC.events?.[name];
        if (!key) continue;
        engine.on(key, (event: any) => {
          const uid = event?.userId || event?.uid || event?.user?.userId;
          markAiIfMatch(uid, name);
        });
      }

      const unpublishEvents = ['onUserUnpublishStream', 'onUserLeave', 'onStreamRemove'];
      for (const name of unpublishEvents) {
        const key = VERTC.events?.[name];
        if (!key) continue;
        engine.on(key, (event: any) => {
          const uid = event?.userId || event?.uid;
          if (uid?.startsWith('ai_interviewer_')) {
            console.log(`[useRTC] AI left via ${name}`);
            setAiPresent(false);
          }
        });
      }

      engine.on(VERTC.events.onLocalAudioPropertiesReport, (reports: any[]) => {
        const own = reports?.[0];
        if (own) setMicLevel(own.audioPropertiesInfo?.linearVolume ?? 0);
      });

      // 字幕兼容路径:onSubtitleMessageReceived 也试一下(SDK 自带 ASR,如果走得通就接住)
      // AIGC 场景下主流是上面的 binary message,这里是 fallback
      const subtitleEventKey = VERTC.events?.onSubtitleMessageReceived;
      if (subtitleEventKey) {
        engine.on(subtitleEventKey, (messages: any[]) => {
          console.log('[useRTC SUBTITLE-DEBUG] onSubtitleMessageReceived', JSON.stringify(messages).slice(0, 300));
          if (!Array.isArray(messages)) return;
          const candidateToken = (window.location.pathname.match(/\/interview\/([^/]+)/) || [])[1];
          if (!candidateToken) return;
          for (const m of messages) {
            if (!m?.text || !m?.definite) continue;
            const role = String(m.userId || '').startsWith('ai_interviewer_') ? 'AI' : '候选人';
            fetch(`/api/interviews/by-token/${candidateToken}/transcript/append`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ role, text: m.text }),
            })
              .then((r) => console.log(`[TranscriptAppend] ok role=${role} len=${m.text.length} status=${r.status}`))
              .catch((e) => console.warn('[useRTC] transcript append failed:', e));
          }
        });
      }

      await engine.joinRoom(
        tokenInfo.token,
        tokenInfo.room_id,
        { userId: tokenInfo.user_id, extraInfo: JSON.stringify({ role: 'candidate' }) },
        {
          isAutoPublish: true,
          isAutoSubscribeAudio: true,
          isAutoSubscribeVideo: false,
          roomProfileType: VERTC.RoomProfileType?.interactivePodcast,
        }
      );

      // 没有摄像头/麦克风硬件的机器(如 Mac mini)上,capture 会失败 —— 不阻塞其他流程
      let canPublish = true;
      try {
        await engine.startVideoCapture();
      } catch (err) {
        console.warn('[useRTC] startVideoCapture failed, continuing without camera:', err);
        canPublish = false;
      }
      try {
        await engine.startAudioCapture();
      } catch (err) {
        console.warn('[useRTC] startAudioCapture failed, continuing without mic:', err);
        canPublish = false;
      }
      if (canPublish) {
        try {
          // MediaType 枚举在不同版本/打包模式下可能不可用;用常量 fallback
          const mt = VERTC.MediaType?.AUDIO_AND_VIDEO ?? 3;  // AUDIO=1, VIDEO=2, AUDIO_AND_VIDEO=3
          await engine.publishStream(mt);
          console.log('[useRTC] publishStream ok, mt=', mt);
        } catch (err) {
          console.warn('[useRTC] publishStream failed:', err);
        }
      } else {
        console.warn('[useRTC] 仅订阅模式(无本机设备)—— 候选人只能听 AI,不能说话或露脸');
      }

      // 入房后主动探测已有用户(AI 可能在我们之前就进来了)
      try {
        const probeMethods = ['getRemoteUsers', 'getUserList', 'getRemoteStreamsInfo'];
        for (const m of probeMethods) {
          if (typeof engine[m] === 'function') {
            const users = await engine[m]();
            console.log(`[useRTC] engine.${m}() →`, users);
            if (Array.isArray(users)) {
              users.forEach((u: any) => markAiIfMatch(u?.userId || u?.uid || u, `probe:${m}`));
            }
          }
        }
      } catch (err) {
        console.warn('[useRTC] probe existing users failed:', err);
      }

      if (localVideoRef.current) {
        engine.setLocalVideoPlayer(0, { renderDom: localVideoRef.current });
      }

      engine.enableAudioPropertiesReport({ interval: 300 });

      // ── 启动客户端录音(只录候选人 mic,不混音)──
      // 之所以不混 AI 远端流:
      //   1. AudioContext + MediaStreamDestination 浮点累加引入 hiss/电流声
      //   2. 远端流从 DOM <audio> 拿,信号链长(decode → reroute → re-encode),引入畸变
      //   3. transcript 已经有 AI 完整文字,录音的产品价值是"听候选人怎么说",AI 不必录
      // 直接 MediaRecorder(mic stream),最短信号链 = 最干净音质。
      try {
        const candidateMic = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,    // 必须开 — 防 RTC feedback(AI 扬声器声音被 mic 录回再发)
            noiseSuppression: true,    // 降底噪
            autoGainControl: false,    // ⚠️ 关闭 — AGC 把信号 push 到 peak 0.26dB 截幅,失真根因
            channelCount: 1,
            sampleRate: 48000,
          },
        });
        localMicStreamRef.current = candidateMic;

        // 直接拿 mic stream 喂 MediaRecorder,不经 AudioContext
        const mime = 'audio/webm;codecs=opus';
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
          const mr = new MediaRecorder(candidateMic, {
            mimeType: mime,
            audioBitsPerSecond: 64000,  // 单声道语音 64kbps 已经很好
          });
          mediaRecorderRef.current = mr;
          recordingChunksRef.current = [];
          mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data); };
          mr.start(2000);
          micStartedAtRef.current = Date.now();  // ← 关键:记录 mic 录音启动时刻
          console.log('[useRTC] recording started (candidate mic, AEC+NS+AGC, 64kbps mono) at', micStartedAtRef.current);
        } else {
          console.warn('[useRTC] MediaRecorder webm/opus not supported');
        }
      } catch (err) {
        console.warn('[useRTC] recording setup failed:', err);
      }

      // ── 豆包 AIGC binary message 监听 ──
      // 实测发现的格式:
      //   "subv" + JSON  → 字幕(ASRResponse / ChatResponse)
      //   ???? + binary  → AI 音频(TTSResponse,event 352)— magic 待实测
      // 候选人和 AI 都会推
      const handleAIGCBinary = (event: any) => {
        const msg = event?.message ?? event;
        // 拿原始 ArrayBuffer
        let buf: ArrayBuffer | null = null;
        if (msg instanceof ArrayBuffer) buf = msg;
        else if (msg?.buffer instanceof ArrayBuffer) buf = msg.buffer;

        // 也尝试解码为字符串(用于 subv 字幕)
        let raw = '';
        try {
          if (typeof msg === 'string') raw = msg;
          else if (buf) raw = new TextDecoder('utf-8', { fatal: false }).decode(buf);
        } catch {}

        // dump 头 16 字节(hex)用于识别格式
        if (buf) {
          const u8 = new Uint8Array(buf);
          const hex = Array.from(u8.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' ');
          const ascii = Array.from(u8.slice(0, 8)).map(b => (b >= 32 && b < 127) ? String.fromCharCode(b) : '.').join('');
          console.log(`[useRTC AIGC-BIN] size=${buf.byteLength} ascii="${ascii}" hex=${hex} userId=${event?.userId}`);

          // ── TTSResponse 音频采集(并行,不影响 mic 录音)──
          // 假设非 "subv" 开头的都是音频流,缓存到 aiAudioChunksRef
          // (实际 magic 待实测,见 console 日志识别后再精确判断)
          if (!raw.startsWith('subv') && !raw.startsWith('conv') && !raw.startsWith('ctrl')) {
            // 不是已知文本协议 → 当作 AI 音频 chunk 缓存
            aiAudioChunksRef.current.push(new Uint8Array(buf));
          }
        }

        if (!raw.startsWith('subv')) {
          return;
        }
        const jsonStart = raw.indexOf('{');
        if (jsonStart < 0) return;
        let parsed: any;
        try { parsed = JSON.parse(raw.slice(jsonStart)); } catch { return; }
        const items: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
        if (!items.length) return;
        const candidateToken = (window.location.pathname.match(/\/interview\/([^/]+)/) || [])[1];
        if (!candidateToken) return;
        for (const m of items) {
          const text = (m?.text || '').trim();
          // 只采纳:有内容 + 是最终稿(definite=true)
          if (!text || !m?.definite) continue;
          const userId = String(m?.userId || '');
          const role = userId.startsWith('ai_interviewer_') ? 'AI' : '候选人';
          console.log(`[useRTC] subtitle [${role}]: ${text.slice(0, 60)}`);
          fetch(`/api/interviews/by-token/${candidateToken}/transcript/append`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role, text }),
          })
            .then((r) => console.log(`[TranscriptAppend] ok role=${role} len=${text.length} status=${r.status}`))
            .catch((e) => console.warn('[useRTC] transcript append failed:', e));
        }
      };

      // 注册:Room 广播 + User 点对点(AIGC 实测走 Room broadcast,但 User 留作兜底)
      for (const name of ['onRoomBinaryMessageReceived', 'onUserBinaryMessageReceived'] as const) {
        const key = VERTC.events?.[name];
        if (key) engine.on(key, handleAIGCBinary);
      }

      setStatus('joined');
    } catch (e: any) {
      console.error('[useRTC] join failed', e);
      setError(e?.message || String(e));
      setStatus('error');
    }
  }, [tokenInfo, isMock, onRemoteAudio]);

  // ⚠️ 已弃用 — 火山服务端自动录制接管(控制台「实时音视频 → 应用管理 → 录制」自动录制规则)
  // 火山服务器在源端混音 → mp3 → 上传 TOS → backend webhook → 落本地
  // 前端 MediaRecorder 录 RTC 远端流 这条路径质量极差(stereo 异步、容器损坏、回声混叠)
  // 保留空函数仅为兼容老调用点,后续可全删
  const hookRemoteAudioToRecording = useCallback((_stream: MediaStream) => {
    // no-op: 云端录制接管,前端不再录 AI 流
  }, []);

  // beforeunload 兜底:用户直接关闭浏览器时,用 sendBeacon 抢救录音
  // sendBeacon 在 unload 时也能可靠发出(同步,fire-and-forget),普通 fetch 在 unload 时会被取消
  useEffect(() => {
    const onBeforeUnload = () => {
      try {
        const mr = mediaRecorderRef.current;
        if (!mr) return;
        // requestData() 同步把当前 buffer 推到 ondataavailable
        if (mr.state === 'recording') {
          try { mr.requestData(); } catch {}
        }
        const chunks = recordingChunksRef.current;
        if (!chunks.length) return;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (blob.size < 1024) return;
        const candidateToken = (window.location.pathname.match(/\/interview\/([^/]+)/) || [])[1];
        if (!candidateToken) return;
        // sendBeacon 可以在 unload 时发出 → 关浏览器也能上传
        navigator.sendBeacon(
          `/api/interviews/by-token/${candidateToken}/recording/upload?ext=webm`,
          blob,
        );
      } catch {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
    };
  }, []);

  // ⚠️ 已弃用 — 火山服务端自动录制接管 AI 音频,前端不再需要录 + 上传
  const stopAndUploadAIAudio = useCallback(async (): Promise<boolean> => true, []);

  // 停录 + 上传 blob 到 backend
  const stopAndUploadRecording = useCallback(async (): Promise<boolean> => {
    const mr = mediaRecorderRef.current;
    if (!mr) return false;
    return new Promise((resolve) => {
      const finalize = async () => {
        try {
          const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
          recordingChunksRef.current = [];
          if (blob.size < 1024) {
            console.warn('[useRTC] recording too small, skip upload', blob.size);
            resolve(false); return;
          }
          const candidateToken = (window.location.pathname.match(/\/interview\/([^/]+)/) || [])[1];
          if (!candidateToken) { resolve(false); return; }
          console.log(`[useRTC] uploading recording, ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
          const r = await fetch(
            `/api/interviews/by-token/${candidateToken}/recording/upload?ext=webm`,
            { method: 'POST', body: blob, headers: { 'Content-Type': 'audio/webm' } },
          );
          const j = await r.json().catch(() => ({}));
          console.log('[useRTC] recording upload result:', r.status, j);
          resolve(r.ok);
        } catch (e) {
          console.warn('[useRTC] upload recording failed:', e);
          resolve(false);
        }
      };
      if (mr.state === 'recording' || mr.state === 'paused') {
        mr.onstop = () => { void finalize(); };
        try { mr.stop(); } catch { void finalize(); }
      } else {
        void finalize();
      }
    });
  }, []);

  const leave = useCallback(async () => {
    // 先并行上传两路:① mic 录音(已有路径)② AI TTSResponse 音频(并行新路径)
    try {
      await Promise.all([
        stopAndUploadRecording(),
        stopAndUploadAIAudio(),
      ]);
    } catch (e) { console.warn(e); }
    // 关闭 mic stream
    try {
      localMicStreamRef.current?.getTracks().forEach((t) => t.stop());
      localMicStreamRef.current = null;
    } catch {}
    // 然后才离 RTC 房间
    try {
      if (engineRef.current && !isMock) {
        await engineRef.current.leaveRoom();
        const mod = (await import('@volcengine/rtc')) as any;
        const VERTC = mod.default ?? mod;
        VERTC.destroyEngine(engineRef.current);
      }
    } catch (e) {
      console.warn('[useRTC] leave error', e);
    } finally {
      engineRef.current = null;
      setStatus('left');
      setAiPresent(false);
    }
  }, [isMock, stopAndUploadRecording, stopAndUploadAIAudio]);

  useEffect(() => {
    if (autoJoin && tokenInfo) void join();
    return () => {
      void leave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo]);

  return {
    status,
    error,
    aiPresent,
    micLevel,
    localVideoRef,
    remoteAudioContainerRef,
    join,
    leave,
    isMock,
    hookRemoteAudioToRecording,
    stopAndUploadRecording,
  };
}
